import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import SectionList from '../components/ui/sections-list';

const pageVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: {
            delay: 0.7,
            staggerChildren: 0.08,
        },
    },
};

const letterVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0 },
};

const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: {
            staggerChildren: 0.1
        }
    }
};

const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.5 }
    }
};

const ActualitesPage = () => {
    const [actualites, setActualites] = useState([]);
    const [filteredActualites, setFilteredActualites] = useState([]);
    const [activeFilter, setActiveFilter] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);

    // Catégories de filtrage
    const filterCategories = [
        { id: 'all', name: 'Toutes' },
        { id: 'entreprise', name: 'Entreprise' },
        { id: 'produit', name: 'Produit' },
        { id: 'événement', name: 'Événement' },
        { id: 'partenariat', name: 'Partenariat' },
    ];

    // Fonction de filtrage
    const filterItems = (category) => {
        setActiveFilter(category);
        if (category === 'all') {
            setFilteredActualites(actualites);
        } else {
            const filtered = actualites.filter(item => item.category === category);
            setFilteredActualites(filtered);
        }
    };

    // Fonction de recherche
    const handleSearch = (event) => {
        const term = event.target.value.toLowerCase();
        setSearchTerm(term);

        let results = activeFilter === 'all'
            ? actualites
            : actualites.filter(item => item.category === activeFilter);

        if (term) {
            results = results.filter(item =>
                item.title.toLowerCase().includes(term) ||
                item.excerpt.toLowerCase().includes(term)
            );
        }

        setFilteredActualites(results);
    };

    useEffect(() => {
        const fetchActualites = async () => {
            try {
                const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/actualite?populate=*`, {
                    headers: {
                        Authorization: `${process.env.REACT_APP_API_TOKEN}`
                    }
                });
                setActualites(response.data.data);
                setFilteredActualites(response.data.data);
                setLoading(false);
            } catch (error) {
                console.error('Error fetching actualites:', error);
                setLoading(false);
            }
        };

        fetchActualites();
    }, []);

    if (loading) return <div>Loading...</div>;

    return (
        <div className='bg-cocoa'>
            <section id="landing-page" className="relative h-screen bg-cocoa overflow-hidden">
                <div className="absolute inset-0 w-full h-full">
                    {/* Image de fond avec effet parallax */}
                    <motion.div
                        initial={{ scale: 1.2 }}
                        animate={{ scale: 1 }}
                        transition={{ duration: 1.5 }}
                        className="relative h-full"
                    >
                        <img
                            src="https://images.pexels.com/photos/3631711/pexels-photo-3631711.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1"
                            alt="Actualités"
                            className="absolute inset-0 w-full h-full object-cover opacity-20"
                        />
                    </motion.div>
                </div>

                {/* Contenu central */}
                <div className="relative h-full max-w-7xl mx-auto px-4">
                    <div className="h-full flex flex-col justify-center items-start">
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8 }}
                            className="max-w-2xl"
                        >
                            <h1 className="text-cream font-cormorant text-5xl md:text-7xl lg:text-8xl mb-6">
                                Nos Actualités
                            </h1>
                            <div className="w-32 h-1 bg-brandy-rose mb-6"></div>
                            <p className="text-cream/80 text-lg md:text-xl max-w-xl">
                                Découvrez les dernières nouvelles, événements et innovations d'ISILIVING.
                                Restez informé de nos projets et de notre évolution.
                            </p>
                        </motion.div>

                        {/* Scroll indicator */}
                        <motion.div
                            className="absolute bottom-10 left-0"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 1, duration: 0.5 }}
                        >
                            <p className="text-cream/60 text-sm mb-2">Scroll</p>
                            <motion.div
                                animate={{ y: [0, 10, 0] }}
                                transition={{ repeat: Infinity, duration: 1.5 }}
                                className="w-px h-16 bg-cream/60"
                            ></motion.div>
                        </motion.div>
                    </div>
                </div>
            </section>

            <motion.section
                className='min-h-screen pb-20'
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={containerVariants}
            >
                {/* Filtres */}
                <div className="flex flex-wrap justify-center gap-2 sm:gap-3 md:gap-4 mb-6 sm:mb-8 px-2 sm:px-4 pt-8">
                    {filterCategories.map(category => (
                        <motion.button
                            key={category.id}
                            onClick={() => filterItems(category.id)}
                            className={`px-3 py-1.5 sm:px-4 sm:py-2 text-sm sm:text-base rounded-full transition-colors ${activeFilter === category.id
                                ? 'bg-brandy-rose text-white'
                                : 'bg-cream text-cocoa hover:bg-brandy-rose hover:text-white'
                                }`}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                        >
                            {category.name}
                        </motion.button>
                    ))}
                </div>

                <div className="w-full max-w-2xl mx-auto px-4 mb-8">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                        className="relative"
                    >
                        <input
                            type="text"
                            placeholder="Rechercher une actualité..."
                            value={searchTerm}
                            onChange={handleSearch}
                            className="w-full px-4 py-3 pl-12 rounded-full bg-cream text-cocoa placeholder-cocoa/50 focus:outline-none focus:ring-2 focus:ring-brandy-rose"
                        />
                        <svg
                            className="absolute left-4 top-1/2 transform -translate-y-1/2 text-cocoa/50"
                            width="20"
                            height="20"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                            />
                        </svg>
                    </motion.div>
                </div>

                <div className='rounded-3xl bg-cream p-8 mx-4 sm:mx-8 md:p-12 lg:px-16 lg:pt-10 lg:pb-6'>
                    <motion.div
                        variants={containerVariants}
                        initial="hidden"
                        animate="visible"
                        className="space-y-4"
                    >
                        {filteredActualites.map((item, index) => (
                            <motion.div
                                key={index}
                                variants={itemVariants}
                                className="card"
                            >
                                <SectionList
                                    id={item.id}
                                    title={item.titre}
                                    description={item.extrait}
                                    images={item.imageCouverture}
                                    date={item.datePublication}
                                    documentId={`/actualites/${item.documentId}`}
                                />
                            </motion.div>
                        ))}
                    </motion.div>
                </div>
            </motion.section>
        </div>
    );
};

export default ActualitesPage;